// pages/FollowRegistrationPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Form, Input, Button, Card, Typography, message, Select, DatePicker,
  Space, Checkbox, Modal, Spin, TimePicker, Divider, Radio, Tag, Upload, Progress,
  Popconfirm, Row, Col
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SoundOutlined,
  RobotOutlined,
  HistoryOutlined,
  SearchOutlined,
  TagOutlined,
  UploadOutlined,
  PaperClipOutlined,
  CloseOutlined,
  ZoomInOutlined,
  CloseCircleFilled,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import locale from 'antd/es/date-picker/locale/ja_JP';

import { ScanResult } from '../types/businessCard';
import { getRoutePath, ROUTES, TEXT_SUMMARY_API, API_BASE_PATH } from '../config';
import { additionalAPI } from '../services/additional-api';
import { useAuth } from '../contexts/AuthContext';
import { getEmergencyMode } from '../utils/chatGptOcr';
import { followAPI, buildNoticeDestinationList, buildInitialNotifRecipients } from '../services/follow-api';

import { FollowRegistrationRequest, NotifRecipientWithDetails } from '../types/follow';
import { AssistInputState, SelectEnqueteInputAidModal } from '../services/assist-input';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { optionFieldAPI, OptionFieldListResponse } from '../services/option-field-api';
import OptionFieldGroup from '../components/OptionFields/OptionFieldGroup';
import CustomerSelectPanel from '../components/CustomerSelectPanel';
import NotifRecipientModal, { AlertSettings } from '../components/NotifRecipientModal';
import { OptionFieldValidator } from '../utils/optionFieldValidation';
import { logApiError } from '../utils/errorLogger';
import { openUrl } from '../utils/linkify';
import { isInFlutter } from '../utils/flutterDetection';
import { isoUtcToJstDate, isFutureDate, isPastDate } from '../utils/dateUtils';
import { previewAttachFile, downloadAttachFile } from '../utils/fileOperations';
import { AppointmentTimePanel, AppointmentInternalAttendeesSection } from '../components/AppointmentTimePanel';
import { Customer } from '../types/businessCard';
import { useContactUsers } from '../hooks/useContactUsers';
import VoiceInputModal, { VoiceInputModalHandle } from '../components/VoiceInputModal';
import FollowAttachmentModal, { FollowAttachFile } from '../components/FollowAttachmentModal';
import KeywordSelectModal from '../components/KeywordSelectModal';
import AssistInputModal from '../components/AssistInputModal';
import BusinessCardImageModal from '../components/BusinessCardImageModal';
import { FORCE_API_ERROR } from '../utils/debugFlags';

// Global declaration for the assist input modal for use in the window object
declare global {
  interface Window {
    SelectEnqueteInputAidModal?: typeof SelectEnqueteInputAidModal;
    State?: typeof AssistInputState;
    enqQuestions?: any[];
    enqCss?: string | any[];
    FlutterCamera?: {
      postMessage: (message: string) => void;
    };
    FlutterNavigation?: {
      postMessage: (message: string) => void;
    };
    FlutterDebug?: {
      postMessage: (message: string) => void;
    };
    FlutterError?: {
      postMessage: (message: string) => void;
    };
    flutterFooterState?: {
      isKeyboardVisible: boolean;
      footerHeight: number;
    };
  }
}



// 録音インジケーターのためのCSS
const recordingAnimationStyle = `
  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
    }

    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(255, 77, 79, 0);
    }

    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
    }
  }

  /* 開始時刻〜終了時刻: 400px以下で3段、400px超で1段 */
  @media (max-width: 400px) {
    .time-range-col { width: 100%; }
    .time-range-sep { width: 100%; text-align: center; }
  }
  @media (min-width: 401px) {
    .time-range-col { width: calc(50% - 16px); flex: 0 0 calc(50% - 16px); max-width: calc(50% - 16px); }
    .time-range-sep { width: auto; flex: 0 0 auto; max-width: none; }
  }
`;

// コンポーネントのインポート
const { Header, Content } = Layout;
const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;


// インターフェース定義
interface FollowFormData {
  date?: dayjs.Dayjs;
  isTodo?: boolean;
  assignee?: string;
  attendees?: string[];
  isVisit?: boolean;
  isCustomerVisit?: boolean;
  scheduledHour?: string;
  scheduledMinute?: string;
  content?: string;
  keyword?: string; // キーワード選択用に追加
  temporaryFlag?: boolean; // 仮登録フラグ
  attachFiles?: File[]; // 添付ファイル
  // 訪問/来社フィールド
  isOnsite?: boolean;
  scheduledTime?: string;
  appointment?: string;
  visitPurpose?: string;
  startTime?: string;
  endTime?: string;
}

// フォローアップ登録ページのコンポーネント
const FollowRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm<FollowFormData>();
  const date = Form.useWatch('date', form);
  const isTodo = Form.useWatch('isTodo', form);
  const [loading, setLoading] = useState<boolean>(false);
  const [skipping, setSkipping] = useState<boolean>(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  // 緊急モード（UseChatGPT=true）。緊急時は追加情報をスキップしてフォロー登録へ来るため、
  // 戻る先を skipadditionalinfoflag と同様に編集画面(EDIT)にする。
  const [emergencyMode, setEmergencyMode] = useState<boolean>(false);
  useEffect(() => {
    // 撮影時に確定した保持値を参照する（このページでは再取得しない）
    getEmergencyMode().then(setEmergencyMode);
  }, []);

  // Flutterフッターメニューの状態（Flutter環境でのみ使用）
  const [flutterFooterHeight, setFlutterFooterHeight] = useState<number>(0);

  const [frontPreviewOpen, setFrontPreviewOpen] = useState<boolean>(false);

  const [aiRewriteModalVisible, setAiRewriteModalVisible] = useState<boolean>(false);
  const [historyModalVisible, setHistoryModalVisible] = useState<boolean>(false);
  const [assistModalVisible, setAssistModalVisible] = useState<boolean>(false);
  const [assistSearch, setAssistSearch] = useState<string>('');
  const [assistOptions, setAssistOptions] = useState<any[]>([]);
  const [selectedAssistEnq, setSelectedAssistEnq] = useState<number | null>(null);
  const [assistContentHtml, setAssistContentHtml] = useState<string>('');
  const [loadingAssistContent, setLoadingAssistContent] = useState<boolean>(false);
  const assistContentRef = useRef<HTMLDivElement>(null);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [defaultFollowAssistNo, setDefaultFollowAssistNo] = useState<number | null>(null);
  const [tempContent, setTempContent] = useState<string>('');
  const [historyData, setHistoryData] = useState<any[]>([]);
  // 履歴取得中フラグ
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const { contactUsers } = useContactUsers();
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  // 次のアクション選択モーダルの表示制御
  const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
  // 本登録モード（仮登録顧客一覧からの編集フロー）。最終モーダルをスキップして一覧へ戻す
  const [isPermanentMode, setIsPermanentMode] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const flag = await indexedDBStorage.getItem('permanentRegistrationMode');
      setIsPermanentMode(flag === 'true');
    })();
  }, []);

  // 本登録モード時に仮登録顧客一覧へ戻す
  const handleReturnToTempCustomerList = async () => {
    await indexedDBStorage.removeItem('permanentRegistrationMode');
    await indexedDBStorage.removeItem('scanResult');
    await indexedDBStorage.removeItem('editResultBackup');
    await indexedDBStorage.setItem('reopenTempCustomerListAfterReturn', 'true');
    navigate(getRoutePath('/menu'));
  };
  const [followNotifModalVisible, setFollowNotifModalVisible] = useState<boolean>(false);
  // 通知先設定モーダルの初期選択受信者
  const [notifInitialRecipients, setNotifInitialRecipients] = useState<NotifRecipientWithDetails[]>([]);
  // TODO: Teams通知実装時はコメントアウトを解除（Teamsチャネル一覧）
  // const [followNotifTeamsChannels, setFollowNotifTeamsChannels] = useState<{ url: string; name: string }[]>([]);
  const voiceModalRef = useRef<VoiceInputModalHandle>(null);
  // 未フォーカスのまま音声入力を「使用する」した場合は末尾に追加する。
  const contentTouchedRef = useRef(false);
  // 手動入力モード
  const [isManualInput, setIsManualInput] = useState<boolean>(false);
  // キーワード関連の状態
  const [keywordModalVisible, setKeywordModalVisible] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<any[]>([]);
  const [keywordsLoading, setKeywordsLoading] = useState<boolean>(false);
  const [keywordSearch, setKeywordSearch] = useState<string>('');
  // 添付ファイル関連の状態
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [fileAddConfirmVisible, setFileAddConfirmVisible] = useState<boolean>(false);
  const [currentFollowNo, setCurrentFollowNo] = useState<number | null>(null);
  const [fileUploadModalVisible, setFileUploadModalVisible] = useState<boolean>(false);
  const [selectedFilesForUpload, setSelectedFilesForUpload] = useState<File[]>([]);
  // 訪問/来社チェックボックスの状態
  const [isOnsite, setIsOnsite] = useState<boolean>(false);
  // 訪問/来社フィールド（AppointmentTimePanelで管理）
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [appointment, setAppointment] = useState<string>('');
  const [visitPurpose, setVisitPurpose] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  // 日付カレンダーの開閉状態
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  // 顧客選択パネルの表示状態
  const [customerSelectVisible, setCustomerSelectVisible] = useState<boolean>(false);
  // 客先同席者（選択された顧客）
  const [onsiteCustomers, setOnsiteCustomers] = useState<Customer[]>([]);
  // 社内同席者（選択されたuserno一覧）
  const [selectedInternalAttendees, setSelectedInternalAttendees] = useState<number[]>([]);

  // Flutterフッター状態の監視（Flutter環境でのみ）
  useEffect(() => {
    if (!isInFlutter()) return;

    const handleFlutterFooterStateChange = (event: any) => {
      const { footerHeight } = event.detail;
      setFlutterFooterHeight(footerHeight || 0);
    };

    // イベントリスナーを登録
    window.addEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);

    // 初期値の取得（Flutterから既に通知されている可能性がある）
    const initialState = (window as any).flutterFooterState;
    if (initialState && initialState.footerHeight !== undefined) {
      setFlutterFooterHeight(initialState.footerHeight);
    }

    return () => {
      window.removeEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);
    };
  }, []);

  // アシスト入力およびキーワードモーダル表示時に背景スクロールを無効化
  useEffect(() => {
    if (assistModalVisible || keywordModalVisible) {
      // モーダル表示時：背景スクロールを無効化
      document.body.style.overflow = 'hidden';
      
      // アシスト入力モーダル用の特別なクラスを追加
      if (assistModalVisible) {
        document.body.classList.add('assist-modal-open');
      } else {
        document.body.classList.remove('assist-modal-open');
      }
    } else {
      // モーダル非表示時：背景スクロールを復元
      document.body.style.overflow = 'unset';
      document.body.classList.remove('assist-modal-open');
    }

    // クリーンアップ関数
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('assist-modal-open');
    };
  }, [assistModalVisible, keywordModalVisible]);
  
  // 処理中の全画面モーダル
  const [processingModalVisible, setProcessingModalVisible] = useState<boolean>(false);
  // アップロード進捗
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadingFileIndex, setUploadingFileIndex] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  // ファイル削除確認用
  const [confirmDeleteFile, setConfirmDeleteFile] = useState<any>(null);
  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState<boolean>(false);
  const [historyAttachModalVisible, setHistoryAttachModalVisible] = useState(false);
  const [historyAttachModalFiles, setHistoryAttachModalFiles] = useState<FollowAttachFile[]>([]);
  const [historyAttachModalFollowIndex, setHistoryAttachModalFollowIndex] = useState<number | null>(null);

  // アシスト入力モーダル内エラー表示用
  const [assistModalError, setAssistModalError] = useState<string>('');
  const assistModalErrorRef = useRef<HTMLDivElement>(null);

  // 拡張フィールドの状態
  const [optionFields, setOptionFields] = useState<OptionFieldListResponse | null>(null);
  const [loadingOptionFields, setLoadingOptionFields] = useState<boolean>(false);
  const [optionFieldValues, setOptionFieldValues] = useState<Record<string, any>>({});

  // 担当者と同席者の選択肢を取得
  const assigneeOptions = contactUsers.map(user => ({
    value: user.userno.toString(),
    label: user.username
  }));

  // 同席者選択肢（担当者と同じだが別変数で定義）
  const attendeeOptions = assigneeOptions;

  // 時間選択肢
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString().padStart(2, '0'),
    label: `${i}時`
  }));

  // 分選択肢
  const minuteOptions = ['00', '15', '30', '45'].map(value => ({
    value,
    label: `${value}分`
  }));

  // モックの履歴データ
  const mockHistoryData = [
    {
      date: '2024-04-03',
      content: '初回訪問。製品Aについて説明し、興味を示された。次回は具体的な提案資料を持参することに。',
      assignee: '山田太郎'
    },
    {
      date: '2024-03-20',
      content: '電話でアポイント取得。4/3に訪問することで合意。',
      assignee: '鈴木次郎'
    },
    {
      date: '2024-02-15',
      content: '展示会で名刺交換。製品に興味を示されたので、後日連絡することに。',
      assignee: '佐藤三郎'
    }
  ];

  // 日付が未来日付なら予定/ToDo を自動ON・ロック、過去日付なら変更不可
  const isFuture = isFutureDate(date);
  const isPast = isPastDate(date);

  useEffect(() => {
    if (isFuture) {
      form.setFieldValue('isTodo', true);
    }
  }, [isFuture]); // eslint-disable-line react-hooks/exhaustive-deps

  // IndexedDBから担当者一覧とフォローNoを取得
  useEffect(() => {
    const getInitialData = async () => {
      // 保存済みのフォローNoがあれば取得
      const storedFollowNo = await indexedDBStorage.getItem('currentFollowNo');
      if (storedFollowNo) {
        try {
          const followNo = Number(storedFollowNo);
          if (!isNaN(followNo)) {
            setCurrentFollowNo(followNo);
          }
        } catch (error) {
          console.error('Failed to parse followNo:', error);
        }
      }
      
      // 拡張フィールド一覧を取得
      setLoadingOptionFields(true);
      try {
        const optionFieldResult = await optionFieldAPI.getOptionFieldList();
        
        if (optionFieldResult.statuscode === 200) {
          setOptionFields(optionFieldResult);
        } else {
          console.error('拡張フィールド取得エラー:', optionFieldResult.message);
          logApiError('FollowRegistrationPage.fetchInitialData', optionFieldResult, '/api/getOptionFieldList', 'GET');
        }
      } catch (optionFieldError) {
        console.error('拡張フィールド取得API呼び出しエラー:', optionFieldError);
        logApiError('FollowRegistrationPage.fetchInitialData', optionFieldError, '/api/getOptionFieldList', 'GET');
      } finally {
        setLoadingOptionFields(false);
      }

      // 手動入力モードかチェック
      const isManualInput = await indexedDBStorage.getItem('isManualInput');
      setIsManualInput(isManualInput === 'true');
    };
    
    getInitialData();
  }, []);

  // アシストオプション取得
  useEffect(() => {
    const fetchAssistOptions = async () => {
      try {
        if (FORCE_API_ERROR) throw new Error('DEBUG: forced error');
        const result = await additionalAPI.getAssistTextList('followContent');
        if (result.success && result.data) {
          // 取得したデータが配列であることを確認
          if (Array.isArray(result.data)) {
            // enqNo と enqName プロパティを持つオブジェクトの配列に変換
            if (typeof result.data[0] === 'string') {
              // 文字列の配列の場合はオブジェクトに変換
              setAssistOptions(result.data.map((text, index) => ({
                enqNo: index + 1,
                enqName: text
              })));
            } else {
              // すでにオブジェクトの配列の場合はそのまま使用
              setAssistOptions(result.data);
            }
            // デフォルトアシストNoをuserから取得し、一覧に存在するものだけセット
            try {
              const userStr = await indexedDBStorage.getItem('user');
              if (userStr) {
                const user = JSON.parse(userStr);
                const defaultNo: number | null = user?.defaultfollowassistno ?? null;

                const isValid =
                  defaultNo !== null &&
                  result.data.some((opt: any) =>
                    typeof opt === 'string' ? false : opt.enqNo === defaultNo
                  );

                setDefaultFollowAssistNo(isValid ? defaultNo : null);
              }
            } catch (e) {
              console.error('デフォルトフォローアシストNo取得エラー:', e);
            }
          } else {
            message.error('アシストの取得に失敗しました。');
            setAssistOptions([]);
          }
        } else {
          console.error('アシスト入力一覧取得エラー:', result.message);
          logApiError('FollowRegistrationPage.fetchAssistOptions', result, '/api/getAssistTextList', 'GET');
          message.error('アシストの取得に失敗しました。');
          setAssistOptions([]);
        }
      } catch (error) {
        console.error('アシスト入力一覧取得APIエラー:', error);
        logApiError('FollowRegistrationPage.fetchAssistOptions', error, '/api/getAssistTextList', 'GET');
        message.error('アシストの取得に失敗しました。');
        setAssistOptions([]);
      }
    };
    
    fetchAssistOptions();
  }, []);

  // 選択されたアシストオプションが変更されたときにAPIを呼び出す
  useEffect(() => {
    const fetchAssistContent = async () => {
      if (selectedAssistEnq !== null) {
        setLoadingAssistContent(true);
        try {
          // 選択したアシストEnqNoを更新（ID要素に設定）
          const hiddenInput = document.getElementById('selectEnqueteInputAid_followContent') as HTMLInputElement;
          if (hiddenInput) {
            hiddenInput.value = selectedAssistEnq.toString();
          }
          
          const result = await additionalAPI.getAssistText(selectedAssistEnq);
          
          if (result.success && result.data) {
            try {
              // デバッグ出力
              
              // データが文字列の場合はJSONパースを試みる
              let parsedObj;
              if (typeof result.data === 'string') {
                parsedObj = JSON.parse(result.data);
              } else {
                parsedObj = result.data;
              }
              
              // エラー確認
              if (parsedObj.statuscode && parsedObj.statuscode !== 200) {
                setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
                return;
              }
              
              // 重要：取得したJSONオブジェクトをSelectEnqueteInputAidModalに直接設定
              SelectEnqueteInputAidModal.enquete = parsedObj;

              // 選択されたアシストオプションのタイトルを設定
              const selectedOption = assistOptions.find(opt => opt.enqNo === selectedAssistEnq);
              if (selectedOption) {
                SelectEnqueteInputAidModal.selectedEnqName = selectedOption.enqName;
              }

              // 質問リストの確認
              let questionList = [];
              if (parsedObj.questionlist) {
                if (Array.isArray(parsedObj.questionlist)) {
                  questionList = parsedObj.questionlist;
                } else {
                  try {
                    // 文字列の場合はJSON解析を試みる
                    if (typeof parsedObj.questionlist === 'string') {
                      questionList = JSON.parse(parsedObj.questionlist);
                    }
                  } catch (e) {
                  }
                }
              } else if (parsedObj.questionInfoList) {
                // 古い形式のAPIレスポンス対応
                if (Array.isArray(parsedObj.questionInfoList)) {
                  questionList = parsedObj.questionInfoList;
                }
              }
              
              // CSS設定の確認
              let css = parsedObj.css || '';
              
              // APIからのCSSと質問HTMLを結合し、スタイルを追加
              const overrideCss = `<style>
  .aptoenq_qa { margin: 0 !important; }
  .whitespace { white-space: pre-wrap; overflow-wrap: break-word; }
  .aptoenq_addtext { max-width: 100%; width: 100%; box-sizing: border-box; }
  textarea { font-family: inherit; font-size: inherit; padding: 5px; border: 1px solid #d9d9d9; border-radius: 4px; width: 100%; max-width: 100%; box-sizing: border-box; }
  label { display: block; margin-bottom: 10px; width: 100%; }
  .aptoenq_a { width: 100%; overflow-x: hidden; }
  .assist-content { width: 100%; overflow-x: hidden; }
  .assist-content * { max-width: 100%; }
</style>`;
              
              // HTML部分は作成せず、純粋な状態のみ保存する
              // 質問はJavaScriptで動的に生成する
              
              // 簡単なプレースホルダーのHTMLだけ設定
              const placeholderHtml = `<style>${typeof css === 'string' ? css : ''}</style>${overrideCss}`;
              setAssistContentHtml(placeholderHtml);
              
              // グローバル変数として質問とCSSを保存
              window.enqQuestions = questionList;
              window.enqCss = css;
              
              // デバッグログ追加
              
              if (questionList && questionList.length > 0) {
              }
              
              // aptoenqform要素を強制的にクリア
              const aptoenqform = document.getElementById('aptoenqform');
              if (aptoenqform) {
                aptoenqform.innerHTML = '';
              }
              
              // 少し待ってから初期化関数を呼び出す（レンダリング完了を待つ）
              setTimeout(() => {
                initializeAssistForm();
              }, 300);
            } catch (e) {
                setAssistContentHtml('<p>データの解析に失敗しました。</p>');
            }
          } else {
            console.error('アシストテキスト取得エラー:', result.message);
            logApiError('FollowRegistrationPage.fetchAssistContent', result, '/api/getAssistText', 'GET');
            setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
          }
        } catch (error) {
          console.error('アシストテキスト取得APIエラー:', error);
          logApiError('FollowRegistrationPage.fetchAssistContent', error, '/api/getAssistText', 'GET');
          setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
        } finally {
          setLoadingAssistContent(false);
        }
      } else {
        setAssistContentHtml('');
      }
    };

    fetchAssistContent();
  }, [selectedAssistEnq]);
  
  // AssistContentHtmlが更新されたら、Stateクラスを初期化する
  useEffect(() => {
    if (assistContentRef.current && assistContentHtml) {
      try {
        
        // グローバルオブジェクトにSelectEnqueteInputAidModalを設定
        window.SelectEnqueteInputAidModal = SelectEnqueteInputAidModal;
        window.State = AssistInputState;
        
        // エラーモーダル表示関数を設定
        (window as any).showAssistErrorModal = (title: string, content: string) => {
          console.log('FollowRegistrationPage: showAssistErrorModal呼び出し', title, content);
          Modal.error({
            title: title,
            content: content,
            okText: 'OK',
            centered: true,
          });
        };
        console.log('FollowRegistrationPage: showAssistErrorModal関数を設定しました');

        // アシスト入力モーダル内でエラーを表示する関数を設定
        (window as any).showAssistErrorInModal = (errorText: string) => {
          console.log('FollowRegistrationPage: showAssistErrorInModal呼び出し', errorText);
          setAssistModalError(errorText);

          // エラー表示位置にスクロール
          setTimeout(() => {
            if (assistModalErrorRef.current) {
              assistModalErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        };
        console.log('FollowRegistrationPage: showAssistErrorInModal関数を設定しました');

        // APIから取得したデータがすでにグローバル変数に保存されているため
        // ここではJSONパースを再度行わない
        
        // コールバック関数を設定
        SelectEnqueteInputAidModal.callback = (text: string) => {
          // フォロー内容のテキストエリアに追加
          const currentContent = form.getFieldValue('content') || '';
          const newContent = currentContent ? `${currentContent}\n${text}` : text;
          form.setFieldsValue({ content: newContent });
          setAssistModalVisible(false);
        };
        
        // アンケート種類を設定
        SelectEnqueteInputAidModal.kind = 'followContent';
        
        // すでにwindow.enqQuestionsとwindow.enqCssが
        // fetchAssistContent内で設定されているので、ここでは設定しない
        
        // API呼び出し側（fetchAssistContent）でinitializeAssistForm()を
        // 呼び出しているため、ここでは呼び出さない
      } catch (error) {
      }
    }
  }, [assistContentHtml, form]);
  
  // 質問データが設定された後に実行する初期化関数
  const initializeAssistForm = async () => {
    // 質問リストの存在チェック
    if (!window.enqQuestions) {
      return;
    }
    
    try {
      
      // まず要素の存在を確認
      let aptoenqform = document.getElementById('aptoenqform');
      
      // モーダル内の表示エリアを探す
      if (!aptoenqform) {
        const container = document.querySelector('.selectEnqueteInputAid .ant-modal-body [style*="border"]');
        if (container) {
          // aptoenqformが存在しない場合は新しく作成
          aptoenqform = document.createElement('div');
          aptoenqform.id = 'aptoenqform';
          aptoenqform.className = 'fontM';
          
          // 既存の要素を確認
          const existingAptoenq = container.querySelector('#aptoenqform');
          if (existingAptoenq) {
            aptoenqform = existingAptoenq as HTMLElement;
          } else {
            // 新しい要素を配置
            container.innerHTML = ''; // 既存のコンテンツをクリア
            container.appendChild(aptoenqform);
          }
        } else {
          message.error('フォーム表示エリアが見つかりません。再度試してください。');
          return;
        }
      }
      
      
      // 要素の内容をクリア
      if (aptoenqform) {
        aptoenqform.innerHTML = '';
      }
      
      // 質問リストの確認と処理
      let processedQuestions = [];
      
      if (Array.isArray(window.enqQuestions)) {
        processedQuestions = window.enqQuestions;
      } else if (typeof window.enqQuestions === 'string') {
        try {
          processedQuestions = JSON.parse(window.enqQuestions);
        } catch(e) {
          processedQuestions = [];
        }
      } else if (window.enqQuestions && typeof window.enqQuestions === 'object') {
        // オブジェクトの場合は値を使用
        const objValues = Object.values(window.enqQuestions);
        if (objValues.length > 0) {
          processedQuestions = objValues;
        }
      }
      
      // 質問リストデータを表示（デバッグ用）
      if (processedQuestions.length > 0) {
      } else {
        return;
      }
      
      // CSSデータの処理
      let cssData = [];
      if (typeof window.enqCss === 'string' && window.enqCss.trim() !== '') {
        // 文字列のCSSをセレクタと値のペアに変換する試み
        try {
          // CSSを簡易的に解析（完全ではありませんが基本的なケースに対応）
          const cssRules = window.enqCss.match(/([^{]+)\s*{\s*([^}]+)\s*}/g) || [];
          cssData = cssRules.map(rule => {
            const [, selector, styleText] = rule.match(/([^{]+)\s*{\s*([^}]+)\s*}/) || [];
            return [selector.trim(), styleText.trim()];
          });
        } catch(e) {
          
        }
      } else if (Array.isArray(window.enqCss)) {
        cssData = window.enqCss;
      }
      
      // 再度要素の存在を確認（念のため）
      aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        message.error('フォーム表示要素の準備に失敗しました');
        return;
      }
      
      // AssistInputStateの新しいインスタンスを作成
      
      // 質問が適切な形式（Array）であることを確認
      if (!Array.isArray(processedQuestions)) {
          message.error('質問データが正しい形式ではありません');
        return;
      }
      
      // 質問が1つ以上あることを確認
      if (processedQuestions.length === 0) {
          message.error('質問が見つかりません');
        return;
      }
      
      try {
        // AssistInputStateの新しいインスタンスを作成
        const state = new AssistInputState(processedQuestions, cssData);
        
        // リセットを呼び出して初期表示
          state.reset();
        
        // 注：ここで特別に質問の表示/非表示を制御する必要はない
        // AssistInputStateクラスのresetメソッドとgetInitialDisplayQuestionsメソッドが
        // 適切な初期表示を行うため、追加の表示設定は必要ない
      } catch (e) {
        message.error('フォームの初期化に失敗しました');
      }
      
      // ボタンを有効化
      const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
      if (resetBtn) {
        resetBtn.removeAttribute('disabled');
      }
      
      const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
      if (selectBtn) {
        selectBtn.removeAttribute('disabled');
      }

      // アシスト入力内のリンクを標準ブラウザで開くようにイベントリスナーを追加
      setTimeout(() => {
        const aptoenqform = document.getElementById('aptoenqform');

        if (aptoenqform) {
          const handleLinkClick = (e: Event) => {
            const target = e.target as HTMLElement;

            if (target.tagName === 'A' && target.hasAttribute('href')) {
              const href = target.getAttribute('href');

              if (href && href !== '#' && (href.startsWith('http://') || href.startsWith('https://'))) {
                e.preventDefault();
                openUrl(href);
              }
            }
          };

          aptoenqform.removeEventListener('click', handleLinkClick);
          aptoenqform.addEventListener('click', handleLinkClick);
        }
      }, 100);
    } catch (error) {
      message.error('初期化中にエラーが発生しました');
    }
  };

  // アシストオプション選択ハンドラ
  const handleAssistOptionChange = (value: number) => {
    setSelectedAssistEnq(value);

    // エラー表示をクリア
    setAssistModalError('');
  };

  // モーダルが表示されたら、aptoenqformを必ずクリアする
  useEffect(() => {
    if (assistModalVisible) {
      // aptoenqform要素を強制的にクリア
      const aptoenqform = document.getElementById('aptoenqform');
      if (aptoenqform) {
        aptoenqform.innerHTML = '';
      }
      
      // グローバルオブジェクトの設定
      window.SelectEnqueteInputAidModal = SelectEnqueteInputAidModal;
      window.State = AssistInputState;
      
      // エラーモーダル表示関数を設定
      (window as any).showAssistErrorModal = (title: string, content: string) => {
        console.log('FollowRegistrationPage (2箇所目): showAssistErrorModal呼び出し', title, content);
        Modal.error({
          title: title,
          content: content,
          okText: 'OK',
          centered: true,
        });
      };
      console.log('FollowRegistrationPage (2箇所目): showAssistErrorModal関数を設定しました');

      // アシスト入力モーダル内でエラーを表示する関数を設定
      (window as any).showAssistErrorInModal = (errorText: string) => {
        console.log('FollowRegistrationPage (2箇所目): showAssistErrorInModal呼び出し', errorText);
        setAssistModalError(errorText);

        // エラー表示位置にスクロール
        setTimeout(() => {
          if (assistModalErrorRef.current) {
            assistModalErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      };
      console.log('FollowRegistrationPage (2箇所目): showAssistErrorInModal関数を設定しました');

      // エラー状態をクリア
      setAssistModalError('');

      // コールバック関数を設定
      SelectEnqueteInputAidModal.callback = (text: string) => {
        // フォロー内容のテキストエリアに追加
        const currentContent = form.getFieldValue('content') || '';
        const newContent = currentContent ? `${currentContent}\n${text}` : text;
        form.setFieldsValue({ content: newContent });
        setAssistModalVisible(false);
      };
      
      // アンケート種類を設定
      SelectEnqueteInputAidModal.kind = 'followContent';
      
      // ドロップダウンをリセット（選択解除）
      const dropdown = document.getElementById('selectEnqueteInputAid_menu') as HTMLSelectElement;
      if (dropdown) {
        dropdown.value = '';
      }
      
      // resetボタンとselectボタンを無効化
      const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
      if (resetBtn) {
        resetBtn.setAttribute('disabled', 'disabled');
      }
      
      const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
      if (selectBtn) {
        selectBtn.setAttribute('disabled', 'disabled');
      }
    }
  }, [assistModalVisible, form]);

  // HTMLコンテンツから直接テキストを挿入する関数
  const insertHtmlContent = () => {
    // 一時的なDOM要素を作成してHTMLを設定
    const tempDiv = assistContentRef.current;
    if (!tempDiv) return;
    
    // <style> タグをすべて削除して、CSSの内容をテキスト抽出対象から除外する
    const styleElements = tempDiv.querySelectorAll('style');
    styleElements.forEach(el => el.remove());
    
    // ラジオボタンなどが選択されている場合はそのテキストを抽出、なければ全体のテキストを使用する
    let selectedText = '';
    const selectedInputs = tempDiv.querySelectorAll('input:checked');
    if (selectedInputs.length > 0) {
      Array.from(selectedInputs).forEach(input => {
        const inputEl = input as HTMLInputElement;
        const enqContainer = inputEl.closest('.aptoenq_qa');
        const parentTitle = enqContainer?.querySelector('.questionTitle')?.textContent?.trim();
        if (parentTitle) {
          selectedText += parentTitle + ': ';
        }
        const parentLabel = inputEl.closest('label');
        if (parentLabel) {
          // ラジオボタンに関連するテキストエリアの値を取得
          const relatedTextarea = parentLabel.querySelector('textarea');
          let textareaValue = '';
          if (relatedTextarea) {
            textareaValue = (relatedTextarea as HTMLTextAreaElement).value;
          }
          
          // ラジオボタンのラベルテキストを取得
          let labelText = '';
          const labelSpan = parentLabel.querySelector('.whitespace');
          if (labelSpan) {
            labelText = labelSpan.textContent?.trim() || '';
          } else {
            labelText = parentLabel.textContent?.trim() || '';
          }
          
          // ラベルテキストとテキストエリアの値を組み合わせる
          selectedText += labelText;
          if (textareaValue) {
            selectedText += ' ' + textareaValue;
          }
          selectedText += ' ';
        } else {
          selectedText += inputEl.value + ' ';
        }
      });
    } else {
      // ラジオボタンが選択されていない場合は、テキストエリアの値があれば取得
      const textareas = tempDiv.querySelectorAll('textarea');
      if (textareas.length > 0) {
        Array.from(textareas).forEach(textarea => {
          const textareaValue = (textarea as HTMLTextAreaElement).value;
          if (textareaValue) {
            selectedText += textareaValue + ' ';
          }
        });
      }
      
      // テキストエリアの値もない場合は全体のテキストを使用
      if (!selectedText) {
        selectedText = tempDiv.textContent || tempDiv.innerText || '';
      }
    }
    
    // 不要な余白を除去して整形
    const cleanedText = selectedText.replace(/\s+/g, ' ').trim();
    
    // 生成したテキストを挿入する
    const currentContent = form.getFieldValue('content') || '';
    const newContent = currentContent ? `${currentContent}\n${cleanedText}` : cleanedText;
    form.setFieldsValue({ content: newContent });
    setAssistModalVisible(false);
  };

  // フィルタリングされたアシストオプションを取得
  const getFilteredAssistOptions = () => {
    if (!assistSearch) return assistOptions;
    return assistOptions.filter(option => 
      option.enqName.toLowerCase().includes(assistSearch.toLowerCase())
    );
  };

  // アシスト検索入力の変更ハンドラ
  const handleAssistSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAssistSearch(e.target.value);
  };
  
  // キーワード検索入力の変更ハンドラ
  const handleKeywordSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywordSearch(e.target.value);
  };
  
  // フィルタリングされたキーワードを取得
  const getFilteredKeywords = () => {
    if (!keywordSearch) return keywords;
    return keywords.filter(keyword => {
      // オブジェクト形式の場合
      if (typeof keyword === 'object' && keyword.keyword) {
        return keyword.keyword.toLowerCase().includes(keywordSearch.toLowerCase());
      }
      // 文字列の場合（互換性のため）
      if (typeof keyword === 'string') {
        return keyword.toLowerCase().includes(keywordSearch.toLowerCase());
      }
      return false;
    });
  };

  // キーワード取得関数
  const fetchKeywords = async () => {
    try {
      setKeywordsLoading(true);
      if (FORCE_API_ERROR) throw new Error('DEBUG: forced error');
      // フォロー登録用キーワード(configFunctionDiv=02)を取得
      const keywordResult = await additionalAPI.getKeywordList("02");
      
      if (keywordResult.success && keywordResult.data) {
        try {
          // keywords が JSON 文字列の場合はパースする
          let keywordData = keywordResult.data;
          if (typeof keywordData === 'string') {
            keywordData = JSON.parse(keywordData);
          }
          
          // keywordlistプロパティがある場合（新しいAPI形式）
          if (keywordData.keywordlist && Array.isArray(keywordData.keywordlist)) {
            // キーワードオブジェクトをそのまま設定（keywordとkeywordcontentプロパティを保持）
            setKeywords(keywordData.keywordlist);
          }
          // 旧APIの形式（result配列がある場合）
          else if (keywordData.result && Array.isArray(keywordData.result)) {
            // 旧形式の場合は新形式に変換
            const keywordList = keywordData.result.map((item: any) => ({
              keyword: item.keywordName || '',
              keywordcontent: item.keywordContent || item.keywordName || ''
            }));
            setKeywords(keywordList);
          } else {
            // データが正しい形式でなかった場合は空配列をセット
            setKeywords([]);
          }
        } catch (parseError) {
          setKeywords([]);
        }
      } else {
        console.error('キーワード取得エラー:', keywordResult.message);
        logApiError('FollowRegistrationPage.handleKeywordChange', keywordResult, '/api/getKeywordList', 'GET');
        message.error('キーワードの取得に失敗しました');
        setKeywords([]);
      }
    } catch (error) {
      console.error('キーワード取得APIエラー:', error);
      logApiError('FollowRegistrationPage.handleKeywordChange', error, '/api/getKeywordList', 'GET');
      message.error('キーワードの取得中にエラーが発生しました');
      setKeywords([]);
    } finally {
      setKeywordsLoading(false);
    }
  };

  useEffect(() => {
    // IndexedDBから撮影結果を取得
    const getScanResult = async () => {
      // contactUsersとoptionFieldsの両方が読み込まれるまで待つ
      if (contactUsers.length === 0 || !optionFields) {
        return;
      }
      
      // 拡張フィールドのデフォルト値設定が完了するまで少し待つ
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const storedResult = await indexedDBStorage.getItem('scanResult');
      
      if (storedResult) {
        try {
          const result: ScanResult = JSON.parse(storedResult);
          setScanResult(result);
          
  
          // ログインユーザーのusernoを取得（デフォルトの担当者に設定）
          const userStr = await indexedDBStorage.getItem('user');
          const user = userStr ? JSON.parse(userStr) : {};
          const defaultAssignee = user?.userno?.toString() || 
                                (contactUsers.length > 0 ? contactUsers[0].userno.toString() : '1');

        // デフォルト値の設定
        form.setFieldsValue({
          date: dayjs(), // Dayjsオブジェクトをそのまま使用
          assignee: defaultAssignee, // ログインユーザーまたは先頭の担当者
          isTodo: false,
          isVisit: true,
          isCustomerVisit: false
        });

        // 履歴データの設定（本来はAPIから取得）
        
      } catch (error) {
        message.error('データの読み込みに失敗しました');
        //navigate(getRoutePath(ROUTES.CAMERA));
      }
    } else {
      message.error('データが見つかりません');
      //navigate(getRoutePath(ROUTES.CAMERA));
    }
    };
    
    getScanResult();
    
    // クリーンアップ関数でIndexedDBからfollowNoを削除
    return () => {
      // コンポーネントのアンマウント時に保存されたフォローNoをクリア
      indexedDBStorage.removeItem('currentFollowNo');
    };
  }, [form, navigate, contactUsers, optionFields]); // optionFieldsも依存関係に追加


  // AI清書モーダルを開く
  const openAiRewriteModal = async () => {
    // フォロー内容を取得
    const currentContent = form.getFieldValue('content') || '';
    
    // 内容が空の場合はアラートを表示
    if (!currentContent.trim()) {
      message.warning('フォロー内容が入力されていません。テキストを入力してから再度お試しください。');
      return;
    }
    
    // AI清書モーダルを表示（処理中の状態で）
    setTempContent('');
    setIsSummarizing(true);
    setAiRewriteModalVisible(true);
    
    try {
      // AI清書APIを呼び出し
      
      const response = await axios.post(TEXT_SUMMARY_API, {
        text: currentContent
      });
      
      
      if (response.data.success) {
        // 結果を設定
        const result = response.data.data.result || '';
        setTempContent(result);
      } else {
        // エラーの場合
        message.error(response.data.message || 'AI清書に失敗しました');
        setTempContent(currentContent); // 元のテキストを設定
        setAiRewriteModalVisible(false); // モーダルを閉じる
      }
    } catch (error: any) {
      console.error('AI清書処理エラー:', error);
      logApiError('FollowRegistrationPage.openAiRewriteModal', error, '/api/text/summary', 'POST');
      message.error('AI清書処理中にエラーが発生しました');
      
      // 開発環境ではモックデータを使用
      if (process.env.NODE_ENV === 'development') {
        setTimeout(() => {
          const mockResult = '先方から製品Aについて問い合わせがあり、機能と価格について説明しました。競合製品と比較した資料を送付することを約束し、来週フォローアップの電話をすることになりました。決裁者は田中部長とのことなので、次回は田中部長も含めた打ち合わせを提案します。';
          setTempContent(mockResult);
        }, 500);
      } else {
        setAiRewriteModalVisible(false); // モーダルを閉じる
      }
    } finally {
      // 処理完了
      setIsSummarizing(false);
    }
  };

  // ファイルプレビュー・ダウンロード処理
  const handleFilePreview = (file: any) => {
    previewAttachFile({ attachfileno: file.attachfileno, filename: file.name || '' });
  };

  const handleFileDownload = (file: any) => {
    downloadAttachFile({ attachfileno: file.attachfileno, filename: file.name || '' });
  };

  const openHistoryModal = async () => {
    try {
      setHistoryLoading(true);
      setHistoryModalVisible(true);
      // 顧客番号を取得
      const editingCustomerNo = await indexedDBStorage.getItem('editingCustomerNo');
      if (!editingCustomerNo) {
        message.error('顧客番号が取得できません');
        return;
      }
      const customerno = parseInt(editingCustomerNo, 10);
      // フォロー一覧をAPIから検索
      let list = await followAPI.searchFollowList({ customerno, excludeTemporary: true });
      // フォロー日の新しい順（降順）にソート。同一の場合はcreatedateで降順
      list = list.sort((a: any, b: any) => {
        const ta = a.followdate ? dayjs(a.followdate).valueOf() : 0;
        const tb = b.followdate ? dayjs(b.followdate).valueOf() : 0;
        if (tb !== ta) {
          return tb - ta;
        }
        // followdateが同一の場合、createdateで降順ソート
        const ca = a.createdate ? dayjs(a.createdate).valueOf() : 0;
        const cb = b.createdate ? dayjs(b.createdate).valueOf() : 0;
        return cb - ca;
      });
      // フォーマットしてstateにセット
      const formatted = list.map((item: any) => {
        const formattedDate = isoUtcToJstDate(item.followdate || '');
        return {
          followno: item.followno || 0,
          date: formattedDate,
          assignee: item.contactusername || item.contactuserno?.toString() || '',
          content: item.followcontent || '',
          // attach フラグ由来（初期表示では添付ファイル取得APIを叩かない）
          hasAttachment: item.attach === true,
          attachments: undefined
        };
      });
      setHistoryData(formatted);
    } catch (error) {
      message.error('履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };


  // AI清書をフォローコンテンツに上書き適用
  const applyAiRewrite = () => {
    // 現在の清書テキストを取得し、フォームのcontentフィールドを上書き
    form.setFieldsValue({ content: tempContent });
    setAiRewriteModalVisible(false);
  };

  // 不要になったため削除
  // API呼び出しはボタンクリック時に直接行うようになりました

  // フォロークリア
  const handleClearFollowForm = () => {
    form.setFieldsValue({
      date: dayjs(),
      content: '',
      temporaryFlag: false,
      isTodo: false,
      isOnsite: false,
    });

    const defaultAssignee = user?.userno?.toString() ||
      (contactUsers.length > 0 ? contactUsers[0].userno.toString() : '1');
    form.setFieldValue('assignee', defaultAssignee);

    setIsOnsite(false);
    setScheduledTime('');
    setAppointment('');
    setVisitPurpose('');
    setStartTime('');
    setEndTime('');
    setOnsiteCustomers([]);
    setSelectedInternalAttendees([]);
    setOptionFieldValues({});

    pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 「スキップ」ボタンのハンドラー
  const handleSkip = async () => {
    try {
      setSkipping(true);
      // 実際にはスキップ処理（APIコールなど）が入ります
      message.success('フォロー登録をスキップしました');
      // 本登録モードの場合は最終モーダルを表示せず一覧へ戻す
      const permFlag = await indexedDBStorage.getItem('permanentRegistrationMode');
      if (permFlag === 'true') {
        await handleReturnToTempCustomerList();
        return;
      }
      // 次のアクション選択モーダルを表示
      setConfirmVisible(true);
    } catch (error) {
      message.error('処理に失敗しました');
    } finally {
      setSkipping(false);
    }
  };

  // 拡張フィールドの値変更ハンドラー
  const handleOptionFieldChange = (fieldKey: string, value: any) => {
    setOptionFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // 「登録」ボタンのハンドラー（通知先設定モーダルを表示）
  const handleSubmit = async () => {
    const editingCustomerNo = await indexedDBStorage.getItem('editingCustomerNo');
    const customerno = editingCustomerNo ? parseInt(editingCustomerNo, 10) : null;
    const assigneeUserno = parseInt(form.getFieldValue('assignee') || '0', 10) || null;
    const isTodoVal = !!form.getFieldValue('isTodo');
    setNotifInitialRecipients(
      await buildInitialNotifRecipients(customerno, assigneeUserno, user?.userno, contactUsers, isTodoVal)
    );
    // TODO: Teams通知実装時はコメントアウトを解除（Teamsチャネル一覧の先取得）
    // if (editingCustomerNo) {
    //   const channels = await followAPI.getTeamsChannels(parseInt(editingCustomerNo, 10));
    //   setFollowNotifTeamsChannels(channels);
    // }
    setFollowNotifModalVisible(true);
  };

  // 通知先設定モーダルの「登録」ボタンのハンドラー
  const handleSubmitFromNotif = async (
    notifRecipients: { userno: number; urgent: boolean }[] = [],
    alertSettings?: AlertSettings,
    // TODO: メールアドレス通知実装時はコメントアウトを解除
    // emailAddresses: string[] = [],
    // TODO: Teams通知実装時はコメントアウトを解除
    // teamsChannelUrls: string[] = []
  ) => {
    setFollowNotifModalVisible(false);
    setLoading(true);
    const followNo = await registerFollowData(false, alertSettings);

    // 通知先（ユーザー）が指定されている場合は通知APIを呼び出す
    // TODO: Teams・メールアドレス通知実装時は以下の条件に `|| teamsChannelUrls.length > 0 || emailAddresses.length > 0` を追加
    const hasRecipients = notifRecipients.length > 0;
    if (followNo && hasRecipients) {
      try {
        // TODO: Teams・メールアドレス通知実装時は buildNoticeDestinationList(notifRecipients, emailAddresses, teamsChannelUrls) に復元
        const noticeDestinationList = buildNoticeDestinationList(notifRecipients);
        const isTodoVal = !!form.getFieldValue('isTodo');
        const alertParams = isTodoVal && alertSettings
          ? {
              plantodoflag: true,
              prealert: alertSettings.preAlert,
              realert: alertSettings.reAlert,
              realertflag: alertSettings.reAlertFlag,
            }
          : undefined;
        const warnMsg = await followAPI.sendNotice(followNo, noticeDestinationList, alertParams);
        // Teams通知失敗時はサーバーから警告メッセージが返る（DB登録は完了）
        if (warnMsg) message.warning(warnMsg);
      } catch {
        // 通知失敗はフォロー登録の成否に影響させない
        message.warning('フォロー情報は登録されましたが、通知の送信に失敗しました');
      }
    }
  };
  
  // フォローNoを使って添付ファイルアップロードモーダルを表示する関数
  const showFileSelectionDialog = (followNo: number) => {
    // ファイル選択モーダルを表示
    
    // 選択されたファイルリストをクリア
    setSelectedFilesForUpload([]);
    
    // アップロードモーダルを表示
    setFileUploadModalVisible(true);
  };
  
  // ファイル選択ハンドラー
  const handleFileSelect = (info: any) => {
    // fileListから実際のFileオブジェクトを取得し、既存リストに追加する（上書きではなく）
    const newFiles = info.fileList
      .filter((file: any) => file.originFileObj)  // 有効なファイルオブジェクトを持つものだけをフィルタリング
      .map((file: any) => file.originFileObj);  // ファイルオブジェクトを取得
    
    // 既にリストにあるファイル名と重複するものは追加しない
    const existingFileNames = selectedFilesForUpload.map(file => file.name);
    const filesToAdd = newFiles.filter((file: File) => !existingFileNames.includes(file.name));
    
    // 既存のファイルに新しいファイルを追加
    setSelectedFilesForUpload([...selectedFilesForUpload, ...filesToAdd]);
    
    // info.fileListをリセットして、アップロードコンポーネントの状態をクリア
    info.fileList = [];
  };
  
  // ファイル選択解除ハンドラー
  const handleRemoveFile = (index: number) => {
    const newFiles = [...selectedFilesForUpload];
    newFiles.splice(index, 1);
    setSelectedFilesForUpload(newFiles);
  };
  
  // ファイルアップロード処理
  const handleFileUpload = async () => {
    if (selectedFilesForUpload.length === 0) {
      message.warning('アップロードするファイルを選択してください');
      return;
    }
    
    // 合計ファイルサイズのチェック (50MB制限)
    const totalSize = selectedFilesForUpload.reduce((total, file) => total + file.size, 0);
    const maxSize = 50 * 1024 * 1024; // 50MB
    
    if (totalSize > maxSize) {
      message.error('ファイルサイズの合計が50MBを超えています');
      return;
    }
    
    // ファイル数の制限チェック
    if (selectedFilesForUpload.length > 10) {
      message.error('一度にアップロードできるファイルは最大10個までです');
      return;
    }
    
    // 初期化
    setUploadProgress(0);
    setUploadingFileIndex(0);
    setUploadingFileName('');
    
    try {
      // フォローNoが必要
      if (!currentFollowNo) {
        throw new Error('フォローNoが取得できません');
      }
      
      // 企業IDの取得（IndexedDBから直接取得）
      let companyId = ""; // デフォルト値
      const savedCompanyId = await indexedDBStorage.getItem('companyId');
      if (savedCompanyId) {
        companyId = savedCompanyId;
      } else {
        // フォールバック: ユーザー情報から取得
        const userStr = await indexedDBStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.selectedCompany && user.selectedCompany.companyid) {
            companyId = user.selectedCompany.companyid.toString();
          }
        }
      }
      
      // 認証トークンの取得
      const token = await indexedDBStorage.getItem('token');
      if (!token) {
        throw new Error('認証情報がありません');
      }
      
      // バックエンドURLを設定
      const backendUrl = `${API_BASE_PATH}/upload_attachment.php`;
      
      // 新しいアップロード用のリスト
      const newFileList = [...fileList];
      const newAttachFiles = [...attachFiles];
      
      // 全てのファイルを順番にアップロード
      for (let i = 0; i < selectedFilesForUpload.length; i++) {
        const fileToUpload = selectedFilesForUpload[i];
        setUploadingFileIndex(i + 1);
        setUploadingFileName(fileToUpload.name);
        setUploadProgress(0);
        
        
        // FormDataの作成
        const formData = new FormData();
        
        // 必須パラメータの追加
        formData.append('type', '1'); // 1: フォロー
        formData.append('targetno', currentFollowNo.toString());
        formData.append('targetoption', ''); // フォローの場合は不要
        formData.append('companyid', companyId);
        
        // ファイルを追加
        formData.append('file', fileToUpload);
        
        try {
          // タイムアウトを設定して、長時間応答がない場合にエラーを表示
          const response = await axios.post(backendUrl, formData, {
            headers: { 
              'Authorization': `Bearer ${token}`
            },
            // アップロード進行状況の監視
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
              } else {
              }
            },
            // タイムアウトを30秒に設定
            timeout: 30000
          });
          
          
          // 結果の確認
          if (response.data.success) {
            // 成功を通知
            message.success(`ファイル「${fileToUpload.name}」をアップロードしました`);
            
            // 添付ファイルリストを更新
            newAttachFiles.push(fileToUpload);
            
            // 表示用リストも更新
            newFileList.push({
              uid: fileToUpload.name,
              name: fileToUpload.name,
              status: 'done',
              size: fileToUpload.size,
              type: fileToUpload.type
            });
          } else {
            // 一部が失敗した場合
            message.error(`ファイル「${fileToUpload.name}」のアップロードに失敗しました`);
          }
        } catch (err) {
          console.error(`ファイル ${i + 1} アップロード中のエラー:`, err);
          logApiError('FollowRegistrationPage.handleFileUpload', err, '/api2/attachfile/upload', 'POST');
          
          if (axios.isAxiosError(err)) {
            if (err.code === 'ECONNABORTED') {
              message.error(`ファイル「${fileToUpload.name}」のアップロードがタイムアウトしました。`);
            } else if (err.response) {
              // サーバーからのエラーレスポンス
              message.error(`ファイル「${fileToUpload.name}」のアップロードエラー: ${err.response.status}`);
            } else if (err.request) {
              // サーバーに到達できない
              message.error('サーバーに接続できません。ネットワーク接続を確認してください。');
              // 通信エラーの場合は残りのファイルもスキップ
              break;
            }
          } else {
            message.error(`ファイル「${fileToUpload.name}」のアップロード中に予期せぬエラーが発生しました`);
          }
        }
        
        // 最後のファイルでない場合は少し待機（サーバー負荷軽減のため）
        if (i < selectedFilesForUpload.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // APIから添付ファイル一覧を取得
      try {
        if (currentFollowNo) {
          const attachmentListResponse = await followAPI.getAttachmentList({
            type: 1, // 1: フォロー
            targetno: currentFollowNo
          });
          
          
          if (attachmentListResponse.statuscode === 200 && attachmentListResponse.attachfilelist) {
            // APIから取得したファイル一覧でUIを更新
            const apiFileList = attachmentListResponse.attachfilelist.map((file: any) => ({
              uid: file.attachfileno.toString(),
              name: file.attachfilename,
              status: 'done',
              size: parseFloat(file.attachfilesize) * 1024,
              type: file.contentType || 'application/octet-stream',
              attachfilekey: file.attachfilekey,
              attachfileno: file.attachfileno
            }));

            // UI用リストを更新
            setFileList(apiFileList);
          } else {
            // APIからの取得に失敗した場合は既存の方法でリストを更新
            setAttachFiles(newAttachFiles);
            setFileList(newFileList);
            message.warning('ファイル一覧の更新に失敗しました');
          }
        } else {
          // フォローNoがない場合は既存の方法でリストを更新
          setAttachFiles(newAttachFiles);
          setFileList(newFileList);
        }
      } catch (error) {
        console.error('ファイル一覧取得エラー:', error);
        // エラー時は既存の方法でリストを更新
        setAttachFiles(newAttachFiles);
        setFileList(newFileList);
        message.warning('ファイル一覧の取得中にエラーが発生しました');
      }
      
      // 選択済みファイルリストをクリア
      setSelectedFilesForUpload([]);
      
      // すべてのファイルのアップロードが完了したことを通知
      message.success(`${selectedFilesForUpload.length}個のファイルのアップロードが完了しました`);
      
      // アップロードモーダルは既に閉じているので何もしない
      
    } catch (error) {
      console.error('ファイルアップロード全体のエラー:', error);
      logApiError('FollowRegistrationPage.handleFileUpload', error, '/api2/attachfile/upload', 'POST');
      
      // より詳細なエラー情報を表示
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // サーバーからのレスポンスがある場合
          console.error('エラーレスポンス:', error.response.data);
          console.error('ステータスコード:', error.response.status);
          message.error(`アップロードエラー (${error.response.status}): ${JSON.stringify(error.response.data)}`);
        } else if (error.request) {
          // リクエストは送信されたがレスポンスがない場合
          console.error('リクエストエラー:', error.request);
          message.error('サーバーからの応答がありません。ネットワーク接続を確認してください。');
        } else {
          // リクエスト設定中のエラー
          message.error(`エラー: ${error.message}`);
        }
      } else {
        // 通常のエラー
        message.error('ファイルのアップロード中にエラーが発生しました');
      }
    }
  };
  
  // フォロー情報を登録する関数（通常登録とファイル添付前の仮登録で共通化）
  const registerFollowData = async (isForAttachment = false, alertSettings?: AlertSettings): Promise<number | null> => {
    try {
      // scanResultが存在するか確認
      if (!scanResult || !scanResult.data) {
        message.error('必要なデータが見つかりません。もう一度撮影してください。');
        return null;
      }

      // フォームのバリデーション
      const values = await form.validateFields();
      
      // 拡張フィールドのバリデーション
      if (optionFields?.followoptionfieldlist) {
        const optionValidationResult = OptionFieldValidator.validateFields(
          optionFieldValues,
          optionFields.followoptionfieldlist
        );
        
        if (!optionValidationResult.isValid) {
          optionValidationResult.errors.forEach(error => {
            message.error(error.message);
          });
          return null;
        }
      }
      
      if (!isForAttachment) {
        setLoading(true);
      }
      
      // 顧客番号の取得
      let editingCustomerNo = await indexedDBStorage.getItem('editingCustomerNo');
      
      // テスト用：顧客番号がない場合でもデバッグで進めるための対策
      if (!editingCustomerNo) {
        if (process.env.NODE_ENV === 'development') {
          // デバッグモードの場合は仮の顧客番号を設定
          editingCustomerNo = 'test-customer-123';
        } else {
          message.error('顧客番号が取得できませんでした');
          return null;
        }
      }
      
      // ユーザー情報からユーザー番号を取得
      const userStr = await indexedDBStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const contactUserNo = values.assignee || user?.userno?.toString() || '';
      
      // 日付をAPIが期待するISO形式に変換（例: 2025-03-04T00:00:00.000+0900）
      const followDate = values.date
        ? values.date.format('YYYY-MM-DD') + 'T00:00:00.000+0900'
        : dayjs().format('YYYY-MM-DD') + 'T00:00:00.000+0900';
      
      // 添付ファイルの処理
      let fileList: string[] = [];
      const uploadPromises: Promise<void>[] = [];
      
      /*if (attachFiles.length > 0) {
        // ファイルのアップロード処理
        for (const file of attachFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('targetno', "1"); // フォロー用
          formData.append('targetoption', customerNo); // 顧客番号
          
          const uploadPromise = axios.post('/api2/attachfile/upload', formData, {
            headers: {
              //'Content-Type': 'multipart/form-data',
            }
          })
          .then(response => {
            if (response.data.success) {
              // アップロードに成功したファイル名を配列に追加
              fileList.push(file.name);
            } else {
              message.error(`ファイル ${file.name} のアップロードに失敗しました`);
            }
          })
          .catch(error => {
            console.error(`Upload error for ${file.name}:`, error);
            logApiError('FollowRegistrationPage.uploadFileToTempStorage', error, '/api2/attachfile/upload', 'POST');
            message.error(`ファイル ${file.name} のアップロード中にエラーが発生しました`);
          });
          
          uploadPromises.push(uploadPromise);
        }
        
        // 全てのファイルアップロードが終わるのを待つ
        await Promise.all(uploadPromises);
      }
        */
      
      // フォローデータを構築
      const followData: FollowRegistrationRequest = {
        customerno: editingCustomerNo,
        followdate: followDate,
        contactuserno: contactUserNo,
        followcontent: values.content || '',
        temporaryflag: values.temporaryFlag || false,
        plantodoflag: values.isTodo || false,
        // フォロー拡張フィールドがある場合はoptionfieldに保存
        optionfield: Object.keys(optionFieldValues).length > 0 ? JSON.stringify(optionFieldValues) : undefined,
        //attachfilelist: fileList.length > 0 ? fileList : undefined
      };

      // アラート設定を追加（予定/ToDoが有効な場合のみ）
      if (alertSettings) {
        followData.prealert = alertSettings.preAlert;
        followData.realert = alertSettings.reAlert;
        followData.realertflag = alertSettings.reAlertFlag;
      }

      // 訪問/来社チェックがONの場合のみ関連フィールドを追加
      if (isOnsite) {
        const [schedHour = '', schedMin = ''] = scheduledTime.split(':');
        const [startHour = '', startMin = ''] = startTime.split(':');
        const [endHour = '', endMin = ''] = endTime.split(':');

        followData.outgovisitflag = true;
        followData.outgovisitschedultimehour = schedHour;
        followData.outgovisitschedultimemin = schedMin;
        followData.outgovisitappoint = appointment;
        followData.outgovisitpurpose = visitPurpose;
        followData.outgovisitstarttimehour = startHour;
        followData.outgovisitstarttimemin = startMin;
        followData.outgovisitendtimehour = endHour;
        followData.outgovisitendtimemin = endMin;

        // 同席者: 客先（attendeesno）・社内（ourattendeesno）を分けてJSON配列文字列でセット
        const customerNos = onsiteCustomers.map((c: any) => c.customerno || c.no).filter(Boolean);
        followData.attendeesno = JSON.stringify(customerNos);
        followData.ourattendeesno = JSON.stringify(selectedInternalAttendees);
      }
      
      // currentFollowNoが存在する場合、更新処理として扱う
      if (currentFollowNo) {
        followData.followno = currentFollowNo.toString();
      } else {
      }
      
      
      // API呼び出し
      const response = await followAPI.registerFollow(followData);
      
      
      if (response.success) {
        // フォローNoがレスポンスに含まれている場合は保持
        let followNo = null;
        if (response.data?.followno) {
          followNo = Number(response.data.followno);
          setCurrentFollowNo(followNo);
          await indexedDBStorage.setItem('currentFollowNo', followNo.toString());
        } else if (response.data?.followdata?.followno) {
          // APIレスポンスに followdata.followno がある場合の対応
          followNo = Number(response.data.followdata.followno);
          setCurrentFollowNo(followNo);
          await indexedDBStorage.setItem('currentFollowNo', followNo.toString());
        } else {
          // レスポンス全体を確認して、どこかにfollownoがないか探す
          const responseStr = JSON.stringify(response);
          
          // 可能性のあるパターンを全て試す
          if (responseStr.includes('"followno":')) {
            try {
              // レスポンス内のJSONから正規表現でfollowNoを抽出
              const match = responseStr.match(/"followno"\s*:\s*(\d+)/);
              if (match && match[1]) {
                followNo = Number(match[1]);
                if (!isNaN(followNo)) {
                  setCurrentFollowNo(followNo);
                  await indexedDBStorage.setItem('currentFollowNo', followNo.toString());
                }
              }
            } catch (e) {
              console.error('Error extracting followNo:', e);
            }
          }
        }
        
        // 通常登録の場合のみメッセージを表示して次のステップへ
        if (!isForAttachment) {
          if (currentFollowNo) {
            message.success('フォロー情報を更新しました');
          } else {
            message.success('フォロー情報を登録しました');
          }
          
          // 保存したデータをクリア
          await indexedDBStorage.removeItem('scanResult');
          await indexedDBStorage.removeItem('editResultBackup');

          // 本登録モードの場合は最終モーダルを表示せず一覧へ戻す
          const permFlag = await indexedDBStorage.getItem('permanentRegistrationMode');
          if (permFlag === 'true') {
            await handleReturnToTempCustomerList();
            return followNo;
          }
          // 次のアクション選択モーダルを表示
          setConfirmVisible(true);
        }
        
        // フォローNoを返す
        return followNo;
      } else {
        console.error('フォロー登録APIエラー:', response.message);
        logApiError('FollowRegistrationPage.handleFinalSubmit', response, '/api/registerFollow', 'POST');
        message.error(response.message || 'フォロー情報の登録に失敗しました');
        
        // 詳細エラーがある場合は追加で表示
        if (response.error) {
          message.error('エラー詳細: ' + response.error);
        }
        
        // エラーはスローせず、ここで処理を完結させる
        return null;
      }
    } catch (error: any) {
      console.error('フォロー登録処理エラー:', error);
      logApiError('FollowRegistrationPage.handleFinalSubmit', error, '/api/registerFollow', 'POST');
      message.error(error.message || '登録に失敗しました');
      return null;
    } finally {
      if (!isForAttachment) {
        setLoading(false);
      }
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* アニメーションのためのスタイル要素 */}
      <style dangerouslySetInnerHTML={{ __html: recordingAnimationStyle }} />
      
      <Header style={{ 
        background: '#fff', 
        padding: '0 16px', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <Button 
          shape="circle"
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={async () => {
            const goEdit = (user?.skipadditionalinfoflag || emergencyMode);
            // 緊急モードで編集画面に戻る場合は、最初の登録時と同様に
            // 「電話番号のみ編集可」のロックを再適用するためのフラグを立てる。
            if (goEdit && emergencyMode) {
              try {
                await indexedDBStorage.setItem('emergencyEditReturnLock', 'true');
              } catch (e) {
                // フラグ保存失敗は致命的でない
              }
            }
            navigate(getRoutePath(goEdit ? ROUTES.EDIT : ROUTES.ADDITIONAL_INFO));
          }}
          style={{
            backgroundColor: 'white',
            borderColor: '#d9d9d9',
            borderWidth: '2px',
            color: '#595959',
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        />
        <div style={{ flex: 1, textAlign: 'center' }}>
          フォロー登録
        </div>
      </Header>

      <Content style={{
        padding: '16px',
        paddingBottom: `${16 + flutterFooterHeight + 80}px`, // 16px基本余白 + Flutterフッター高さ + 固定ボタン高さ(約80px)
        background: '#f0f2f5',
        overflowY: 'auto'
      }}>
        <div ref={pageTopRef} />
        {/* 読み取った名刺画像 */}        
        {scanResult?.data?.businesscardfrontimage && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-block', position: 'relative' }}>
                <img
                  src={scanResult.data.businesscardfrontimage}
                  alt="撮影した名刺"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    display: 'block'
                  }}
                />
                <button
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0, 0, 0, 0.45)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setFrontPreviewOpen(true)}
                >
                  <ZoomInOutlined />
                </button>
              </div>
            </div>
          </Card>
        )}
        
        {/* フォロー情報入力フォーム */}
        <Card style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{
              isTodo: false,
              assignee: '1',
              isVisit: true,
              isCustomerVisit: false,
            }}
          >
            {/* 訪問/来社 チェックボックス */}
            <Form.Item name="isOnsite" valuePropName="checked">
              <Checkbox onChange={e => setIsOnsite(e.target.checked)}>訪問/来社</Checkbox>
            </Form.Item>

            {/* 予定/ToDo チェックボックス */}
            <Form.Item name="isTodo" valuePropName="checked">
              <Checkbox disabled={isFuture || (isPast && !isTodo)}>予定/ToDo</Checkbox>
            </Form.Item>

            {/* 日付フィールド */}
            <Form.Item
              name="date"
              label="日付"
              rules={[{ required: true, message: '日付を入力してください' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY/MM/DD"
                locale={locale}
                open={datePickerOpen}
                onOpenChange={setDatePickerOpen}
              />
            </Form.Item>

            {/* 訪問/来社 展開エリア */}
            <AppointmentTimePanel
              isOnsite={isOnsite}
              scheduledTime={scheduledTime}
              onScheduledTimeChange={setScheduledTime}
              appointment={appointment}
              onAppointmentChange={setAppointment}
              visitPurpose={visitPurpose}
              onVisitPurposeChange={setVisitPurpose}
              startTime={startTime}
              onStartTimeChange={setStartTime}
              endTime={endTime}
              onEndTimeChange={setEndTime}
              onsiteCustomers={onsiteCustomers}
              onOnsiteCustomerRemove={id =>
                setOnsiteCustomers(prev => prev.filter(c => c.customerno !== id && c.no !== id))
              }
              onOnsiteCustomersClear={() => setOnsiteCustomers([])}
              onCustomerSelectOpen={() => setCustomerSelectVisible(true)}
            />


            {/* 担当プルダウン */}
            <Form.Item
              name="assignee"
              label="担当"
              rules={[{ required: true, message: '担当者を選択してください' }]}
            >
              <Select placeholder="担当者を選択">
                {assigneeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* 同席者（社内） */}
            <AppointmentInternalAttendeesSection
              isOnsite={isOnsite}
              selectedInternalAttendees={selectedInternalAttendees}
              onSelectedInternalAttendeesChange={setSelectedInternalAttendees}
              contactUsers={contactUsers}
              excludeCurrentUserNo={user?.userno}
            />

            {/* 同席者プルダウン */}
            {/*
            <Form.Item
              name="attendees"
              label="同席者"
            >
              <Select
                mode="multiple"
                placeholder="同席者を選択"
                allowClear
              >
                {attendeeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            */}

            {/* 履歴確認ボタン */}
            <Form.Item>
              <Button 
                type="default" 
                icon={<HistoryOutlined />} 
                onClick={openHistoryModal}
                style={{ marginBottom: 16 }}
              >
                履歴確認
              </Button>
            </Form.Item>

            {/* 訪問・来社チェックボックス */}
            {/*
            <Form.Item label="訪問形式">
              <Space>
                <Form.Item name="isVisit" valuePropName="checked" noStyle>
                  <Checkbox>訪問</Checkbox>
                </Form.Item>
                <Form.Item name="isCustomerVisit" valuePropName="checked" noStyle>
                  <Checkbox>来社</Checkbox>
                </Form.Item>
              </Space>
            </Form.Item>
            */}

            {/* 予定時刻 */}
            {/*
            <Form.Item label="予定時刻">
              <Space>
                <Form.Item name="scheduledHour" noStyle>
                  <Select style={{ width: 80 }} placeholder="時">
                    {hourOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="scheduledMinute" noStyle>
                  <Select style={{ width: 80 }} placeholder="分">
                    {minuteOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Space>
            </Form.Item>
            /*}

            {/* フォロー内容 テキストエリア - 仮登録チェックボックスと同じ行に */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div>フォロー内容</div>
              <Form.Item name="temporaryFlag" valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox>仮登録</Checkbox>
              </Form.Item>
            </div>
            <Form.Item
              name="content"
            >
              <TextArea
                id="follow-reg-content-textarea"
                rows={6}
                placeholder="フォロー内容を入力"
                onFocus={() => { contentTouchedRef.current = true; }}
              />
            </Form.Item>


            {/* アシスト入力・音声入力・AI入力ボタン */}
            <Form.Item>
              <Space size="middle" style={{ display: 'flex', flexWrap: 'wrap' }}>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />} 
                  onClick={() => {
                    setSelectedAssistEnq(defaultFollowAssistNo);
                    setAssistModalVisible(true);
                    
                    // Add mobile keyboard handling for assist modal
                    if (window.innerWidth <= 768) {
                      // Save current scroll position
                      const originalScrollY = window.scrollY;
                      
                      const setupKeyboardHandling = () => {
                        // Find the assist input modal specifically (should be the topmost modal)
                        const modals = document.querySelectorAll('.ant-modal');
                        const modal = modals[modals.length - 1]; // Get the last (topmost) modal
                        if (!modal) return;
                        
                        const handleInputFocus = (input: Element) => {
                          const modalElement = modal as HTMLElement;
                          modalElement.style.position = 'fixed !important';
                          modalElement.style.top = '0px !important';
                          modalElement.style.left = '0 !important';
                          modalElement.style.right = '0 !important';
                          modalElement.style.margin = '0 auto !important';
                          modalElement.style.transform = 'none !important';
                          modalElement.style.zIndex = '9999 !important';
                          modalElement.style.width = 'auto !important';
                          modalElement.style.maxWidth = '90vw !important';
                          
                          // Find assist input content area and scroll input to top
                          setTimeout(() => {
                            const modalBody = modal.querySelector('.ant-modal-body') as HTMLElement;
                            const aptoenqform = modal.querySelector('#aptoenqform') as HTMLElement;
                            
                            if (modalBody && aptoenqform && input instanceof HTMLElement) {
                              // Force reflow to ensure correct positioning
                              void modalBody.offsetHeight;
                              
                              // Try multiple methods to ensure input is visible
                              // Method 1: scrollIntoView on the input element
                              try {
                                input.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start',
                                  inline: 'nearest'
                                });
                              } catch (e) {
                                // Fallback: manual scroll calculation
                                const inputTop = input.offsetTop || 0;
                                const targetScrollTop = Math.max(0, inputTop - 50);
                                
                                modalBody.scrollTo({
                                  top: targetScrollTop,
                                  behavior: 'smooth'
                                });
                              }
                            }
                          }, 300);
                        };
                        
                        const addFocusListeners = () => {
                          // Target only inputs within the assist content area
                          const inputs = modal.querySelectorAll('#aptoenqform input, #aptoenqform textarea, .ant-modal-body input, .ant-modal-body textarea');
                          inputs.forEach(input => {
                            input.removeEventListener('focus', () => handleInputFocus(input));
                            input.addEventListener('focus', () => handleInputFocus(input));
                          });
                        };
                        
                        addFocusListeners();
                        
                        const observer = new MutationObserver(() => {
                          setTimeout(addFocusListeners, 300);
                        });
                        
                        observer.observe(modal, {
                          childList: true,
                          subtree: true
                        });
                        
                        // Store original scroll position for restoration
                        (modal as any)._originalScrollY = originalScrollY;
                      };
                      
                      setTimeout(setupKeyboardHandling, 1500);
                      setTimeout(setupKeyboardHandling, 3000);
                      setTimeout(setupKeyboardHandling, 5000);
                    }
                  }}
                  size="middle"
                  style={{ 
                    height: '36px', 
                    paddingLeft: '16px', 
                    paddingRight: '16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  アシスト入力
                </Button>
                <Button 
                  type="primary" 
                  icon={<SoundOutlined />} 
                  onClick={() => voiceModalRef.current?.start()}
                  size="middle"
                  style={{ 
                    height: '36px', 
                    paddingLeft: '16px', 
                    paddingRight: '16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  音声入力
                </Button>
                <Button 
                  type="primary" 
                  icon={<RobotOutlined />} 
                  onClick={openAiRewriteModal}
                  size="large"
                  style={{ 
                    height: '50px', 
                    paddingLeft: '20px', 
                    paddingRight: '20px',
                    fontSize: '16px',
                    display: 'none', /* AI清書ボタンを非表示 */
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  AI清書
                </Button>                
                <Button 
                  type="primary" 
                  icon={<TagOutlined />} 
                  onClick={() => {
                    fetchKeywords();
                    setKeywordModalVisible(true);
                  }}
                  size="large"
                  style={{ 
                    height: '50px', 
                    paddingLeft: '20px', 
                    paddingRight: '20px',
                    fontSize: '16px',
                    display: 'none', /* キーワード入力ボタンを非表示 */
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  キーワード入力
                </Button>
              </Space>
            </Form.Item>
            
            
            {/* 仮登録チェックボックスはフォロー内容のラベル部分に移動 */}
            
            {/* 添付ファイル項目 - 仮登録の下に配置 */}
            <Form.Item
              label="添付ファイル"
            >
              {/* カスタムファイルリスト表示 */}
              {fileList.length > 0 ? (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>添付ファイル一覧:</div>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: '4px', padding: '0 8px', position: 'relative', zIndex: 5 }}>
                    {fileList.map((file, index) => (
                      <div
                        key={file.uid || file.name}
                        style={{
                          padding: '8px',
                          marginBottom: index < fileList.length - 1 ? '8px' : 0,
                          borderBottom: index < fileList.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                      >
                        {/* 1段目: クリップアイコン + ファイル名 + 容量 */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                          <PaperClipOutlined style={{ marginRight: '8px' }} />
                          <span>{file.name}</span>
                          <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>
                            {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                          </span>
                        </div>
                        {/* 2段目: プレビュー・ダウンロード・削除ボタン */}
                        <div style={{ display: 'flex', marginTop: '6px', gap: '4px' }}>
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            style={{ flex: 1 }}
                            onClick={() => handleFilePreview(file)}
                          />
                          <Button
                            type="text"
                            icon={<DownloadOutlined style={{ color: '#1677ff' }} />}
                            style={{ flex: 1, backgroundColor: '#e6f4ff' }}
                            onClick={() => handleFileDownload(file)}
                          />
                          <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                            style={{ flex: 1, backgroundColor: '#fff0f0' }}
                            onClick={() => {
                              setConfirmDeleteFile(file);
                              setConfirmDeleteModalVisible(true);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              
              {/* ファイル追加ボタン */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                <Upload
                  beforeUpload={(file) => {
                    // 実際のFileオブジェクトを保存
                    setAttachFiles([...attachFiles, file]);
                    
                    // Uploadコンポーネント用のリストを更新
                    setFileList([...fileList, {
                      uid: file.uid || file.name,
                      name: file.name,
                      status: 'done',
                      size: file.size,
                      type: file.type
                    }]);
                    return false;
                  }}
                  fileList={[]} // 空のリストを渡して、Uploadコンポーネント自体のファイルリスト表示を無効化
                  showUploadList={false} // ファイルリストを表示しない
              >
                <Button 
                  icon={<UploadOutlined />}
                  size="large"
                  style={{ 
                    height: '44px',
                    paddingLeft: '16px',
                    paddingRight: '16px'
                  }}
                  onClick={async (e) => {
                    e.stopPropagation(); // アップロードダイアログが開くのを防止
                    
                    // 現在のfollowNo状態をコンソールに出力（デバッグ用）
                    
                    // 念のためにIndexedDBから直接読み込み
                    const storedFollowNo = await indexedDBStorage.getItem('currentFollowNo');
                    
                    // followNoが既に取得済みの場合は保存確認モーダルをスキップ
                    if (currentFollowNo) {
                      // 直接ファイルアップロードモーダルを表示
                      showFileSelectionDialog(currentFollowNo);
                    } else if (storedFollowNo) {
                      // stateにないがIndexedDBにある場合も直接ファイルアップロードモーダルを表示
                      const followNo = Number(storedFollowNo);
                      if (!isNaN(followNo)) {
                        setCurrentFollowNo(followNo);
                        showFileSelectionDialog(followNo);
                      } else {
                        setFileAddConfirmVisible(true);
                      }
                    } else {
                      // まだfollowNoがない場合は保存確認モーダルを表示
                      setFileAddConfirmVisible(true);
                    }
                  }}
                >
                  ファイル追加
                </Button>
                </Upload>
              </div>
            </Form.Item>
          </Form>
        </Card>

        {/* 拡張フィールド表示 */}
        {optionFields?.followoptionfieldlist && optionFields.followoptionfieldlist.length > 0 && (
          <div className="option-field-group">
            <OptionFieldGroup
              title=""
              fields={optionFields.followoptionfieldlist}
              values={optionFieldValues}
              onFieldChange={handleOptionFieldChange}
              prefix="follow"
            />
          </div>
        )}

        {/* 内容をクリアボタン */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48 }}>
          <Button
            onClick={handleClearFollowForm}
            style={{ width: '60%', backgroundColor: '#ffffff' }}
          >
            内容をクリア
          </Button>
        </div>
      </Content>

      {/* フラットなフルワイドボタンエリア */}
      <div style={{
        display: 'flex',
        position: 'sticky',
        bottom: isInFlutter() ? `${flutterFooterHeight}px` : 0,
        width: '100%',
        zIndex: 10
      }}>
        <Button 
          type="default" 
          onClick={handleSkip}
          loading={skipping}
          style={{ 
            flex: '1 1 50%', 
            height: '56px', 
            borderRadius: 0,
            borderTop: '1px solid #e8e8e8',
            borderRight: '1px solid #e8e8e8',
            borderBottom: 'none',
            borderLeft: 'none',
            boxShadow: 'none'
          }}
        >
          スキップ
        </Button>
        <Button 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={handleSubmit}
          loading={loading}
          style={{ 
            flex: '1 1 50%', 
            height: '56px', 
            borderRadius: 0,
            borderTop: '1px solid #0693e3',
            borderBottom: 'none',
            boxShadow: 'none'
          }}
        >
          フォロー登録
        </Button>
      </div>
      
      <VoiceInputModal
        ref={voiceModalRef}
        tempContent={tempContent}
        onTempContentChange={setTempContent}
        onUse={(text) => {
          const textarea = document.getElementById(
            'follow-reg-content-textarea',
          ) as HTMLTextAreaElement | null;
          const current = (form.getFieldValue('content') as string) || '';
          if (textarea) {
            // 本文に一度でもフォーカスしていればカーソル位置に挿入（範囲選択時は置換）、
            // 未フォーカスなら末尾に追加する。
            const start = contentTouchedRef.current ? textarea.selectionStart : current.length;
            const end = contentTouchedRef.current ? textarea.selectionEnd : current.length;
            const newContent = current.substring(0, start) + text + current.substring(end);
            form.setFieldsValue({ content: newContent });
            setTimeout(() => {
              textarea.focus();
              textarea.setSelectionRange(start + text.length, start + text.length);
            }, 0);
          } else {
            form.setFieldsValue({ content: current + text });
          }
        }}
      />
      
      {/* AI清書モーダル */}
      <Modal
        title="AI清書"
        open={aiRewriteModalVisible}
        onCancel={() => {
          setAiRewriteModalVisible(false);
          setIsSummarizing(false);
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setAiRewriteModalVisible(false);
              setIsSummarizing(false);
            }}
            disabled={isSummarizing}
          >
            キャンセル
          </Button>,
          <Button 
            key="submit"
            type="primary" 
            onClick={() => {
              // 追加処理
              const currentContent = form.getFieldValue('content') || '';
              const newContent = currentContent ? `${currentContent}\n${tempContent}` : tempContent;
              form.setFieldsValue({ content: newContent });
              setAiRewriteModalVisible(false);
              setIsSummarizing(false);
            }}
            disabled={!tempContent || isSummarizing}
          >
            追加
          </Button>,
          <Button 
            key="overwrite" 
            type="primary" 
            onClick={applyAiRewrite}
            disabled={!tempContent || isSummarizing}
            style={{ marginLeft: 8 }}
          >
            上書き
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">テキストをAI清書した結果です。内容を確認して「追加」ボタンを押すと既存の内容に追加、「上書き」ボタンを押すと既存の内容を置き換えます。</Text>
        </div>
        {isSummarizing ? (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <Spin size="large" tip="AI清書処理中..." />
            <div style={{ marginTop: 15, color: '#888' }}>
              テキストをAI処理しています。しばらくお待ちください...
            </div>
          </div>
        ) : (
          <TextArea 
            rows={8} 
            value={tempContent}
            onChange={(e) => setTempContent(e.target.value)}
            placeholder="AI清書結果がここに表示されます"
            style={{ marginBottom: 10 }}
          />
        )}
      </Modal>
      
      {/* 履歴確認モーダル */}
      <Modal
        title="フォロー履歴"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setHistoryModalVisible(false)}>
            閉じる
          </Button>
        ]}
        width={700}
      >
        <Spin spinning={historyLoading} tip="履歴を取得中...">
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {historyData.map((history, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16 }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{`${history.date} (${history.assignee})`}</span>
                    {(history as any).hasAttachment && (
                      <Button
                        type="text"
                        size="small"
                        icon={<PaperClipOutlined style={{ color: '#1677ff' }} />}
                        style={{ backgroundColor: '#e6f4ff' }}
                        onClick={async () => {
                          let attachments = (history as any).attachments;
                          if (!attachments) {
                            try {
                              const res = await followAPI.getAttachmentList({ type: 1, targetno: (history as any).followno });
                              attachments = (res.attachfilelist || []).map((file: any) => ({
                                attachfileno: file.attachfileno,
                                name: file.attachfilename || file.filename || file.name || '',
                                size: parseFloat(file.attachfilesize || '0') * 1024,
                                type: file.contentType || file.filetype || file.type || ''
                              }));
                              setHistoryData(prev => prev.map((h, i) =>
                                i === index ? { ...h, attachments } : h
                              ));
                            } catch (error) {
                              console.error('添付ファイル取得エラー:', error);
                              message.error('添付ファイルの取得に失敗しました');
                              return;
                            }
                          }
                          setHistoryAttachModalFiles(attachments || []);
                          setHistoryAttachModalFollowIndex(index);
                          setHistoryAttachModalVisible(true);
                        }}
                      />
                    )}
                  </div>
                }
                size="small"
              >
                <div className="whitespace">{history.content}</div>
              </Card>
            ))}
            {historyData.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                履歴データがありません
              </div>
            )}
          </div>
        </Spin>
      </Modal>

      {/* フォロー履歴 添付ファイルモーダル */}
      <FollowAttachmentModal
        open={historyAttachModalVisible}
        files={historyAttachModalFiles}
        onClose={() => setHistoryAttachModalVisible(false)}
        onFilesChange={setHistoryAttachModalFiles}
        onFileDeleted={(attachfileno) => {
          if (historyAttachModalFollowIndex !== null) {
            setHistoryData(prev => prev.map((h, i) => {
              if (i === historyAttachModalFollowIndex) {
                const newAttachments = h.attachments?.filter((a: any) => a.attachfileno !== attachfileno);
                return { ...h, attachments: newAttachments, hasAttachment: (newAttachments?.length ?? 0) > 0 };
              }
              return h;
            }));
          }
        }}
        onFilePreview={handleFilePreview}
        onFileDownload={handleFileDownload}
        confirmZIndex={1001}
      />

      {/* アシスト入力モーダル */}
      <AssistInputModal
        open={assistModalVisible}
        onClose={() => {
          setAssistModalVisible(false);
          setSelectedAssistEnq(null);
        }}
        options={assistOptions}
        selectedValue={selectedAssistEnq}
        onValueChange={handleAssistOptionChange}
        contentHtml={assistContentHtml}
        loading={loadingAssistContent}
        error={assistModalError}
        errorRef={assistModalErrorRef}
        contentRef={assistContentRef}
        hiddenInputId="selectEnqueteInputAid_followContent"
        width={600}
        bodyStyle={{ overflow: 'hidden', padding: '2px' }}
      />
      {/* 登録完了後の確認モーダル */}
      <Modal
        centered={false}
        open={confirmVisible}
        title={isManualInput
          ? '次の顧客情報を入力しますか？'
          : '次の名刺を読み取りますか？'}
        okText="Yes"
        cancelText="No"
        wrapClassName="centered-modal"
        bodyStyle={{
          textAlign: 'center'
        }}
        getContainer={() => document.body}
        onOk={async () => {
          // IndexedDBのクリア: 次セッションは新規フロー
          await indexedDBStorage.removeItem('scanResult');
          await indexedDBStorage.removeItem('editingCustomerNo');
          await indexedDBStorage.removeItem('editResultBackup');
          await indexedDBStorage.removeItem('isManualInput');
          setConfirmVisible(false);

          if (isManualInput) {
            // 手動入力モード
            await indexedDBStorage.setItem('isManualInput', 'true');
            // 空のscanResultを作成して保存
            const emptyScanResult = {
                success: true,
                data: {},
                apiResponse: null
            };
            await indexedDBStorage.setItem('scanResult', JSON.stringify(emptyScanResult));
            await indexedDBStorage.setItem('editResultBackup', JSON.stringify(emptyScanResult));

            navigate(getRoutePath(ROUTES.EDIT));
          } else {
            // 通常撮影モードの場合はカメラへ
            navigate(getRoutePath(ROUTES.CAMERA));
          }
        }}
        onCancel={async () => {
          // IndexedDBのクリア: 次セッションは新規フロー
          await indexedDBStorage.removeItem('scanResult');
          await indexedDBStorage.removeItem('editingCustomerNo');
          await indexedDBStorage.removeItem('editResultBackup');
          await indexedDBStorage.removeItem('isManualInput');
          setConfirmVisible(false);
          navigate(getRoutePath(ROUTES.MENU));
        }}
      />
      
      {/* ファイル追加前の確認モーダル */}
      <Modal
        centered
        open={fileAddConfirmVisible}
        title="確認"
        okText="はい"
        cancelText="いいえ"
        onOk={async () => {
          // モーダルを閉じる
          setFileAddConfirmVisible(false);
          
          // 処理中モーダルを表示
          setProcessingModalVisible(true);
          
          try {
            // loading状態をリセット（別のローディングインジケータを使用）
            setLoading(false);
            
            // フォロー情報を登録
            const followNo = await registerFollowData(true);
            
            if (followNo) {
              // APIから正しくフォローNoが取得できた場合
              setProcessingModalVisible(false);
              message.success('フォロー情報を保存しました');
              showFileSelectionDialog(followNo);
            } else {
              // 何らかの理由でフォローNoが取得できなかった場合
              setProcessingModalVisible(false);
              message.error('フォロー情報の保存に失敗しました');
              setFileUploadModalVisible(false);
            }
          } finally {
            setProcessingModalVisible(false);
          }
        }}
        onCancel={() => {
          // キャンセル時はモーダルを閉じるだけ
          setFileAddConfirmVisible(false);
        }}
      >
        <p>現在の状態を保存します。よろしいですか？</p>
      </Modal>
      
      {/* ファイルアップロードモーダル */}
      <Modal
        title="添付ファイル追加"
        open={fileUploadModalVisible}
        onCancel={() => setFileUploadModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setFileUploadModalVisible(false)}>
            閉じる
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={() => {
              setProcessingModalVisible(true);
              // プログレスモーダルが表示されたらファイルアップロードモーダルを非表示に
              setFileUploadModalVisible(false);
              handleFileUpload().finally(() => {
                setProcessingModalVisible(false);
              });
            }}
            disabled={selectedFilesForUpload.length === 0}
          >
            アップロード
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <Upload
            multiple
            beforeUpload={() => false} // アップロードを防止し、ファイル選択のみに使用
            onChange={handleFileSelect}
            showUploadList={false} // アップロードリストは自前で表示するので非表示
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.zip" // 許可するファイル形式
            fileList={[]} // 常に空のリストを渡して内部状態をリセット
          >
            <Button 
              icon={<UploadOutlined />} 
              size="large"
              style={{
                height: '40px',
                padding: '0 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ファイルを選択
            </Button>
          </Upload>
        </div>
        
        <div style={{ marginTop: '16px', color: '#888' }}>
          １度に最大10個までアップロード可能です。ファイルサイズの合計は50MB以下としてください。
        </div>
        
        {selectedFilesForUpload.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>
              選択済みファイル:
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '0' }}>
              {selectedFilesForUpload.map((file, index) => (
                <div 
                  key={index} 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '10px 8px',
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                    minHeight: '48px'
                  }}
                >
                  <div style={{ 
                    flex: 1, 
                    wordBreak: 'break-all',
                    paddingRight: '8px',
                    lineHeight: '1.4'
                  }}>
                    <span style={{ marginRight: '8px', fontWeight: 'bold' }}>{index + 1}.</span>
                    {file.name}
                  </div>
                  <div style={{ 
                    marginLeft: '16px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    flexShrink: 0 
                  }}>
                    <span style={{ 
                      minWidth: '70px', 
                      textAlign: 'right', 
                      color: '#888', 
                      marginTop: '2px' 
                    }}>
                      {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    </span>
                    <Button 
                      type="text" 
                      danger
                      icon={<CloseOutlined />} 
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      style={{ marginLeft: '8px' }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              backgroundColor: '#f6f6f6', 
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 'bold'
            }}>
              <span>合計: {selectedFilesForUpload.length}ファイル</span> 
              <span style={{ color: '#1890ff' }}>
                {(selectedFilesForUpload.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          </div>
        )}
      </Modal>
      
      {/* 処理中の全画面モーダル */}
      <Modal
        title={uploadingFileIndex > 0 ? "ファイルアップロード中" : "処理中"}
        open={processingModalVisible}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        style={{ textAlign: 'center' }}
      >
        <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Spin size="large" />
          
          {uploadingFileIndex > 0 ? (
            <>
              <div style={{ marginTop: '20px', fontSize: '16px', fontWeight: 'bold' }}>
                {uploadingFileName}
              </div>
              <div style={{ marginTop: '10px', width: '100%' }}>
                <Progress percent={Math.round((uploadingFileIndex / selectedFilesForUpload.length) * 100)} status="active" />
              </div>
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#888' }}>
                {uploadingFileIndex} / {selectedFilesForUpload.length} ファイルをアップロード中...
              </div>
            </>
          ) : (
            <div style={{ marginTop: '20px', fontSize: '16px' }}>
              しばらくお待ちください...
            </div>
          )}
        </div>
      </Modal>

      {/* ファイル削除確認モーダル */}
      <Modal
        title="確認"
        open={confirmDeleteModalVisible}
        onCancel={() => setConfirmDeleteModalVisible(false)}
        centered
        width={400}
        footer={[
          <Button key="cancel" onClick={() => setConfirmDeleteModalVisible(false)}>
            いいえ
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            onClick={async () => {
              if (!confirmDeleteFile) return;
              setConfirmDeleteModalVisible(false);
              
              try {
                // 処理中メッセージを表示
                message.loading('ファイルを削除しています...', 0);
                
                if (currentFollowNo && (confirmDeleteFile as any).attachfileno) {
                  // 企業IDの取得
                  let companyId = "";
                  const savedCompanyId = await indexedDBStorage.getItem('companyId');
                  if (savedCompanyId) {
                    companyId = savedCompanyId;
                  } else {
                    // フォールバック: ユーザー情報から取得
                    const userStr = await indexedDBStorage.getItem('user');
                    if (userStr) {
                      const user = JSON.parse(userStr);
                      if (user.selectedCompany && user.selectedCompany.companyid) {
                        companyId = user.selectedCompany.companyid.toString();
                      }
                    }
                  }
                  
                  if (!companyId) {
                    throw new Error('企業IDが取得できませんでした');
                  }
                  
                  // 添付ファイルの削除APIリクエスト実行
                  await followAPI.deleteAttachment({
                    attachfileno: (confirmDeleteFile as any).attachfileno,
                    companyid: companyId
                  });
                }
                
                // 実際のFileオブジェクトを更新
                const newAttachFiles = attachFiles.filter(item => item.name !== confirmDeleteFile.name);
                setAttachFiles(newAttachFiles);
                
                // Uploadコンポーネント用のリストを更新
                const newFileList = fileList.filter(item => item.uid !== confirmDeleteFile.uid);
                setFileList(newFileList);
                
                // 処理中メッセージを非表示
                message.destroy();
                
                // 成功メッセージを表示
                message.success('ファイルを削除しました');
                
                // フォローNoがある場合は添付ファイル一覧を再取得
                if (currentFollowNo) {
                  try {
                    const attachmentListResponse = await followAPI.getAttachmentList({
                      type: 1, // 1: フォロー
                      targetno: currentFollowNo
                    });
                    
                    if (attachmentListResponse.statuscode === 200 && attachmentListResponse.attachfilelist) {
                      // APIから取得したファイル一覧でUIを更新
                      const apiFileList = attachmentListResponse.attachfilelist.map((apiFile: any) => ({
                        uid: apiFile.attachfileno.toString(),
                        name: apiFile.attachfilename,
                        status: 'done',
                        size: parseFloat(apiFile.attachfilesize) * 1024,
                        type: apiFile.contentType || 'application/octet-stream',
                        attachfilekey: apiFile.attachfilekey,
                        attachfileno: apiFile.attachfileno
                      }));
                      
                      // UI用リストを更新
                      setFileList(apiFileList);
                    }
                  } catch (error) {
                    console.error('ファイル一覧の再取得に失敗しました:', error);
                  }
                }
              } catch (error) {
                console.error('ファイル削除エラー:', error);
                // 処理中メッセージを非表示
                message.destroy();
                // エラーメッセージを表示
                message.error('ファイルの削除中にエラーが発生しました');
              }
            }}
          >
            はい
          </Button>,
        ]}
      >
        <p>「{confirmDeleteFile?.name}」を削除してもよろしいですか？</p>
      </Modal>

      {/* 名刺表面 拡大プレビュー */}
      <BusinessCardImageModal
        open={frontPreviewOpen}
        src={scanResult?.data?.businesscardfrontimage}
        alt="撮影した名刺"
        flutterFooterHeight={flutterFooterHeight}
        onClose={() => setFrontPreviewOpen(false)}
      />
      {/* 顧客選択パネル（同席者追加） */}
      <CustomerSelectPanel
        visible={customerSelectVisible}
        onClose={() => setCustomerSelectVisible(false)}
        defaultCompany={scanResult?.data?.company}
        onSelectionChange={customers => setOnsiteCustomers(customers)}
        selectedCustomersFromParent={onsiteCustomers}
      />

      {/* フォロー登録の確認モーダル */}
      <NotifRecipientModal
        open={followNotifModalVisible}
        onCancel={() => setFollowNotifModalVisible(false)}
        // TODO: Teams・メールアドレス通知実装時は handleSubmitFromNotif(recipients, teamsChannelUrls, emailAddresses, alertSettings) に復元
        onSubmit={(recipients, _teams, _emails, alertSettings) => {
          handleSubmitFromNotif(recipients, alertSettings);
        }}
        contactUsers={contactUsers}
        initialRecipients={notifInitialRecipients}
        // TODO: Teams通知実装時はコメントアウトを解除
        // teamsChannels={followNotifTeamsChannels}
        title="フォロー登録の確認"
        submitLabel="登録"
        showAlertSettings={isTodo ?? false}
      />
    </Layout>
  );
};

export default FollowRegistrationPage;